import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Box, Card, CardMedia, Link, Typography} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {ChatImageViewer} from './ChatImageViewer';

const normalizeImageUrl = value => {
	try {
		if (!String(value || '').trim()) return '';
		const browserUrl = typeof window !== 'undefined' ? window.location.href : 'https://localhost/';
		const url = new URL(String(value || ''), browserUrl);
		const sameOriginHttp = typeof window !== 'undefined' &&
			url.protocol === 'http:' && url.origin === window.location.origin;
		return url.protocol === 'https:' || sameOriginHttp ? url.href : '';
	} catch (error) {
		return '';
	}
};

const normalizeChatImages = (images, failedImageUrls) => (Array.isArray(images) ? images : [])
	.map(image => {
		if (!image || typeof image !== 'object') return null;
		const url = normalizeImageUrl(image.url);
		if (!url || failedImageUrls.includes(url)) return null;
		return {
			...image,
			url,
			sourceUrl: normalizeImageUrl(image.sourceUrl)
		};
	})
	.filter(Boolean)
	.slice(0, 6);

const getImageTitle = (image, index, labels) => image.title || image.caption || image.sourceTitle || image.alt ||
	`${labels.image} ${index + 1}`;

export function ChatImageGallery({images, labels}) {
	const [previewImage, setPreviewImage] = useState(null);
	const [failedImageUrls, setFailedImageUrls] = useState([]);
	const [compact, setCompact] = useState(false);
	const galleryRef = useRef(null);
	const chatImages = useMemo(
		() => normalizeChatImages(images, failedImageUrls),
		[images, failedImageUrls]
	);

	useEffect(() => {
		const element = galleryRef.current;
		if (!element) return undefined;
		const updateCompact = () => setCompact(element.getBoundingClientRect().width < 520);
		updateCompact();
		if (typeof ResizeObserver === 'undefined') return undefined;
		const observer = new ResizeObserver(updateCompact);
		observer.observe(element);
		return () => observer.disconnect();
	}, [chatImages.length]);

	if (chatImages.length === 0) return null;

	return (
		<>
			<Box
				ref={galleryRef}
				display="grid"
				gridTemplateColumns={compact || chatImages.length === 1
					? 'minmax(0, 1fr)'
					: 'repeat(2, minmax(0, 1fr))'}
				gap={1.5}
				mt={1.5}
			>
				{chatImages.map((image, index) => {
					const title = getImageTitle(image, index, labels);
					return (
						<Card key={image.id || image.url} variant="outlined" sx={{borderRadius: 2, overflow: 'hidden', width: '100%', maxWidth: 760}}>
							<Box
								component="button"
								type="button"
								onClick={() => setPreviewImage({...image, title})}
								aria-label={labels.openImage}
								title={labels.openImage}
								sx={{
									display: 'block', width: '100%', p: 0, border: 0, cursor: 'zoom-in',
									bgcolor: 'grey.50', lineHeight: 0
								}}
							>
								<CardMedia
									component="img"
									loading="lazy"
									image={image.url}
									alt={image.alt || title}
									onError={() => setFailedImageUrls(value => value.includes(image.url) ? value : [...value, image.url])}
									sx={{width: '100%', height: compact ? 160 : 'auto', maxHeight: compact ? 160 : 420, objectFit: 'contain'}}
								/>
							</Box>
							<Box px={1.5} py={1.25}>
								<Typography variant="body2" color="text.primary">{title}</Typography>
								{image.sourceUrl ? (
									<Link href={image.sourceUrl} target="_blank" rel="noopener noreferrer" variant="caption">
										{image.sourceLabel || labels.imageSource}<OpenInNewIcon fontSize="inherit" sx={{ml: 0.4, verticalAlign: 'middle'}} />
									</Link>
								) : null}
							</Box>
						</Card>
					);
				})}
			</Box>
			<ChatImageViewer image={previewImage} onClose={() => setPreviewImage(null)} labels={labels} />
		</>
	);
}
